import { IncrementalSha256 } from './incremental-sha256';

export const QUOTE_ENGINE_EVAL_HASH_CHUNK_BYTES = 4 * 1024 * 1024;

export interface QuoteEngineEvalHashProgress {
    bytesHashed: number;
    totalBytes: number;
}

export interface QuoteEngineEvalFileHashOptions {
    signal?: AbortSignal;
    chunkSize?: number;
    /** Set false for deterministic test/non-worker environments. */
    preferWorker?: boolean;
    /** Injectable only so worker construction failure can be proven in tests. */
    workerFactory?: () => Worker;
    onChunk?: (progress: QuoteEngineEvalHashProgress) => void;
}

interface HashWorkerMessage {
    type: 'progress' | 'result' | 'error';
    bytesHashed?: number;
    totalBytes?: number;
    sha256?: string;
    name?: string;
    message?: string;
}

function cancellationError(): DOMException {
    return new DOMException('Quote Engine file hashing cancelled.', 'AbortError');
}

function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) throw cancellationError();
}

function normalizedChunkSize(value: number | undefined): number {
    const size = value ?? QUOTE_ENGINE_EVAL_HASH_CHUNK_BYTES;
    if (!Number.isSafeInteger(size) || size < 1) {
        throw new RangeError('Quote Engine hash chunk size must be a positive safe integer.');
    }
    return size;
}

async function readBlobChunk(blob: Blob, signal?: AbortSignal): Promise<Uint8Array> {
    throwIfAborted(signal);
    if (typeof FileReader !== 'function') {
        const bytes = new Uint8Array(await blob.arrayBuffer());
        throwIfAborted(signal);
        return bytes;
    }

    return new Promise<Uint8Array>((resolve, reject) => {
        const reader = new FileReader();
        let settled = false;
        const cleanup = () => signal?.removeEventListener('abort', onSignalAbort);
        const finish = (operation: () => void) => {
            if (settled) return;
            settled = true;
            cleanup();
            operation();
        };
        const onSignalAbort = () => {
            try {
                reader.abort();
            } catch {
                // Some DOM shims throw when abort races a completed read.
            }
            finish(() => reject(cancellationError()));
        };
        reader.onload = () => finish(() => {
            if (signal?.aborted) {
                reject(cancellationError());
                return;
            }
            if (!(reader.result instanceof ArrayBuffer)) {
                reject(new Error('Quote Engine hash chunk did not produce binary data.'));
                return;
            }
            resolve(new Uint8Array(reader.result));
        });
        reader.onerror = () => finish(() => reject(
            reader.error || new Error('Quote Engine hash chunk could not be read.'),
        ));
        reader.onabort = () => finish(() => reject(cancellationError()));
        signal?.addEventListener('abort', onSignalAbort, { once: true });
        if (signal?.aborted) {
            onSignalAbort();
            return;
        }
        reader.readAsArrayBuffer(blob);
    });
}

export async function hashFileOnMainThread(
    file: File,
    options: QuoteEngineEvalFileHashOptions = {},
): Promise<string> {
    const chunkSize = normalizedChunkSize(options.chunkSize);
    const digest = new IncrementalSha256();
    throwIfAborted(options.signal);

    for (let offset = 0; offset < file.size; offset += chunkSize) {
        throwIfAborted(options.signal);
        const end = Math.min(offset + chunkSize, file.size);
        const chunk = await readBlobChunk(file.slice(offset, end), options.signal);
        digest.update(chunk);
        options.onChunk?.({ bytesHashed: end, totalBytes: file.size });
    }

    throwIfAborted(options.signal);
    return digest.digestHex();
}

function createDefaultWorker(): Worker {
    return new Worker(
        new URL('../workers/quote-engine-eval-hash.worker.ts', import.meta.url),
        { type: 'module', name: 'quote-engine-eval-hash' },
    );
}

async function hashFileInWorker(
    file: File,
    chunkSize: number,
    options: QuoteEngineEvalFileHashOptions,
): Promise<string> {
    throwIfAborted(options.signal);
    const worker = (options.workerFactory || createDefaultWorker)();

    return new Promise<string>((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
            options.signal?.removeEventListener('abort', onAbort);
            worker.terminate();
        };
        const finish = (operation: () => void) => {
            if (settled) return;
            settled = true;
            cleanup();
            operation();
        };
        const onAbort = () => {
            try {
                worker.postMessage({ type: 'cancel' });
            } catch {
                // Termination below is the fail-safe cancellation boundary.
            }
            finish(() => reject(cancellationError()));
        };
        worker.onmessage = (event: MessageEvent<HashWorkerMessage>) => {
            const message = event.data;
            if (message?.type === 'progress') {
                try {
                    options.onChunk?.({
                        bytesHashed: Number(message.bytesHashed || 0),
                        totalBytes: Number(message.totalBytes || file.size),
                    });
                } catch (error) {
                    finish(() => reject(error));
                }
                return;
            }
            if (message?.type === 'result') {
                if (/^[a-f0-9]{64}$/.test(message.sha256 || '')) {
                    finish(() => resolve(message.sha256 as string));
                } else {
                    finish(() => reject(new Error('Quote Engine hash worker returned an invalid SHA-256 digest.')));
                }
                return;
            }
            if (message?.type === 'error') {
                const error = message.name === 'AbortError'
                    ? cancellationError()
                    : Object.assign(
                        new Error(message.message || 'Quote Engine hash worker failed.'),
                        { name: message.name || 'Error' },
                    );
                finish(() => reject(error));
            }
        };
        worker.onerror = (event: ErrorEvent) => finish(() => reject(
            event.error || new Error(event.message || 'Quote Engine hash worker failed.'),
        ));
        options.signal?.addEventListener('abort', onAbort, { once: true });
        if (options.signal?.aborted) {
            onAbort();
            return;
        }
        try {
            worker.postMessage({ type: 'hash', file, chunkSize });
        } catch (error) {
            finish(() => reject(error));
        }
    });
}

/**
 * Hash a File without materialising the whole payload. Vite workers keep the
 * multi-GB loop off the UI thread; unsupported/failed worker environments use
 * the same incremental algorithm and fixed chunk boundaries on the main thread.
 */
export async function hashQuoteEngineEvalFile(
    file: File,
    options: QuoteEngineEvalFileHashOptions = {},
): Promise<string> {
    if (!(file instanceof Blob)) throw new TypeError('Quote Engine hashing requires a File or Blob.');
    const chunkSize = normalizedChunkSize(options.chunkSize);
    throwIfAborted(options.signal);

    const canUseWorker = options.preferWorker !== false
        && (options.workerFactory !== undefined || typeof Worker === 'function');
    if (canUseWorker) {
        try {
            return await hashFileInWorker(file, chunkSize, options);
        } catch (error) {
            if ((error as Error)?.name === 'AbortError' || options.signal?.aborted) throw error;
            // Worker startup/runtime support varies across Chrome shells and
            // test harnesses. Recompute deterministically with bounded chunks.
        }
    }
    return hashFileOnMainThread(file, { ...options, chunkSize });
}
